import { useState, MouseEvent, ReactNode } from 'react';
import { CircularProgress, Menu, MenuItem } from '@material-ui/core'; 
import Button from 'shared/components/Button';
import { useSnackbar } from 'notistack'; 
import { TransferReservationOpenedModel } from '../../models/TransferReservationOpened';

interface ActionsItemOpenedProps {
  row: TransferReservationOpenedModel;
  onReAssignDriver: (driver: any, withContingency: boolean) => void;
  getActiveContingencies: () => Promise<any[]>;
  onShowTravelDetails: (id: number) => ReactNode;
  onEditReservation: (row: TransferReservationOpenedModel) => void;
  onCancelReservation: (row: TransferReservationOpenedModel) => void;
  onReservationCopy: (row: TransferReservationOpenedModel) => void;
  onForceEnd: (row: TransferReservationOpenedModel) => void;
  copyReservationPublicNewLinkToClipboard: (id: number) => Promise<void>;
  setOpenToPending: (id: number) => void;
  onDelayNotify: (row: TransferReservationOpenedModel) => void;
  onBusesPreAssign: (row: TransferReservationOpenedModel) => void;
}

export function ActionsItemOpened({
  row,
  onReAssignDriver,
  getActiveContingencies,
  onShowTravelDetails,
  onEditReservation,
  onCancelReservation,
  onReservationCopy,
  onForceEnd,
  copyReservationPublicNewLinkToClipboard,
  setOpenToPending,
  onDelayNotify,
  onBusesPreAssign
}: ActionsItemOpenedProps) {
  const snackbar = useSnackbar();

  // Estados locales encapsulados por fila
  const [reAssignAnchorEl, setReAssignAnchorEl] = useState<HTMLElement | null>(null);
  const [reservationAnchorEl, setReservationAnchorEl] = useState<HTMLElement | null>(null);
  const [isLoadingReAssign, setIsLoadingReAssign] = useState(false);
  const [hasActiveContingencyReAssign, setHasActiveContingencyReAssign] = useState(false);

  const handleReAssignClick = async (ev: MouseEvent<HTMLButtonElement>) => {
    ev.stopPropagation();
    const target = ev.currentTarget;
    setIsLoadingReAssign(true);
    try {
      const actives = await getActiveContingencies();
      setHasActiveContingencyReAssign(actives.length > 0);
    } catch {
      setHasActiveContingencyReAssign(false);
      snackbar.enqueueSnackbar('Error al verificar contingencias activas', {
        variant: 'error'
      });
    } finally {
      setIsLoadingReAssign(false);
      setReAssignAnchorEl(target);
    }
  };

  return (
    <div className="flex flex-col justify-between text-sm">
      {!row.bus_requirement && (
        <>
          <Button
            onClick={handleReAssignClick}
            variant='dashed'
            className="mb-2 w-full"
          >
            <span className="flex items-center gap-2 whitespace-nowrap">
              Re-asignar
              {isLoadingReAssign && (
                <CircularProgress size={12} style={{ color: 'inherit' }} />
              )}
            </span>
          </Button>

          <Menu
            anchorEl={reAssignAnchorEl}
            open={Boolean(reAssignAnchorEl)}
            onClose={(ev: MouseEvent) => {
              ev.stopPropagation();
              setReAssignAnchorEl(null);
            }}
          >
            <MenuItem
              onClick={(ev) => {
                ev.stopPropagation();
                setReAssignAnchorEl(null);
                onReAssignDriver(row, false);
              }}
            >
              Re-asignar
            </MenuItem>
            {hasActiveContingencyReAssign && (
              <MenuItem
                onClick={(ev) => {
                  ev.stopPropagation();
                  setReAssignAnchorEl(null);
                  onReAssignDriver(row, true);
                }}
              >
                Re-asignar con Contingencia
              </MenuItem>
            )}
          </Menu>
        </>
      )}

      <Button
        onClick={(ev) => setReservationAnchorEl(ev.currentTarget)}
        variant='dashed'
        className="w-full"
      >
        Reserva
      </Button>

      <Menu
        anchorEl={reservationAnchorEl}
        open={Boolean(reservationAnchorEl)}
        onClose={() => setReservationAnchorEl(null)}
      >
        {onShowTravelDetails(row.id)}

        <MenuItem onClick={() => onEditReservation(row)}>Modificar</MenuItem>
        <MenuItem onClick={() => onCancelReservation(row)}>Cancelar</MenuItem>
        <MenuItem onClick={() => onReservationCopy(row)}>Copiar</MenuItem>
        <MenuItem onClick={() => onForceEnd(row)}>Finalizar</MenuItem>
        <MenuItem
          onClick={async () => {
            await copyReservationPublicNewLinkToClipboard(row.id);
            setReservationAnchorEl(null);
            snackbar.enqueueSnackbar('Link de seguimiento copiado', {
              variant: 'info'
            });
          }}
        >
          Copiar link de seguimiento
        </MenuItem>
        <MenuItem onClick={() => setOpenToPending(row.id)}>
          Volver a Pendiente
        </MenuItem>
        <MenuItem
          onClick={() => {
            setReservationAnchorEl(null);
            onDelayNotify(row);
          }}
        >
          Notificar Demora
        </MenuItem>
      </Menu>

      {row.bus_requirement && (
        <Button
          onClick={() => onBusesPreAssign(row)}
          variant='dashed'
          className="mt-2 w-full whitespace-nowrap"
        >
          Editar SG
        </Button>
      )}
    </div>
  );
}

export default ActionsItemOpened;